<template>
  <section
    v-if="
      status !== 'success' || (recentCampsites && recentCampsites.length > 0)
    "
    :class="{ desktop: $isDesktop }"
    :style="{ minHeight: status === 'success' ? undefined : '500px' }"
    class="recently-viewed-campsites"
  >
    <translate tag="h3" v-if="status === 'success'"
      >Recently viewed sites</translate
    >
    <ul
      v-if="recentCampsites && recentCampsites.length > 0"
      class="recently-viewed"
    >
      <li
        v-for="campsite of recentCampsites || []"
        :key="campsite.slug"
        class="recent"
      >
        <pu-campsite-link
          :slug="campsite.slug"
          :hierarchy-path="campsite.path"
          exclude-type="campsiteLink"
          :exclude-query-params="['sa', 'ved']"
          :filters="{ page: undefined, sort: undefined }"
          :force-no-filters="shouldForceNoFilters"
          @click="emit('select')"
        >
          <div
            class="image-frame"
            :style="{
              backgroundImage: 'url(' + getRecentImageUrl(campsite) + ')',
            }"
          />
          <div class="cs-details">
            <pu-rating-bubble
              :rating="campsite.rating"
              :rate-count="campsite.rateCount"
              class="rating-bubble"
              @click.prevent
            />
            <span class="cs-name">{{ campsite.name }}</span>
            <p class="cs-location">
              {{ campsite.hierarchyText }}
            </p>
            <time-ago class="cs-viewed-time" :date="campsite.visitDate">
              Viewed
            </time-ago>
          </div>
        </pu-campsite-link>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import type { Pinia } from 'pinia'
import { computed } from 'vue'
import { RecentCampsiteDetails } from '../../../apps/recent-campsites/types'
import { useRecentCampsitesStore } from '../../../stores/useRecentCampsitesStore'

const props = defineProps<{
  limit?: number
}>()

const router = useRouter()
const pinia = usePinia() as Pinia
const recentCampsitesStore = useRecentCampsitesStore(pinia)
const error = useError()
const errorStatusCode = computed(() => error.value?.statusCode || null)
const { $isDesktop } = useNuxtApp()
const runtimeConfig = useRuntimeConfig()

const { data: recentCampsites, status } = useAsyncData(
  () =>
    recentCampsitesStore.refreshCampsites(
      router.currentRoute.value.params.lang?.toString() || 'en-gb',
      props.limit ?? 6,
    ),
  {
    server: false,
  },
)

const shouldForceNoFilters = computed(() => errorStatusCode.value === 404)

const emit = defineEmits<{
  (e: 'select'): void
}>()

const getRecentImageUrl = (campsite: RecentCampsiteDetails): string => {
  if (!campsite?.primaryPhoto?.masterImage) {
    return `https://${runtimeConfig.public.envUrl}/app-icons/pitchup-icon-192x192.png`
  }
  return `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${campsite.primaryPhoto.masterImage}`
}
</script>

<style scoped>
.recently-viewed-campsites {
  padding: 1.4em 1.4em 0;
  border-top: 4px solid var(--colour-page-background);
  overflow: hidden;
  width: 100vw;
  position: relative;
  isolation: isolate;
}

.recently-viewed-campsites h3 {
  font-weight: normal;
  color: var(--colour-wcag-blue);
  font-size: 1.2em;
  margin: 0 0 1em 0;
}

.recently-viewed {
  font-size: var(--body);
  list-style: none;
  margin: 0 0 2em 0;
  padding: 0;
  overflow: visible;
  width: 100%;
}

.recently-viewed li a {
  color: #333;
  display: flex;
  flex-direction: row;
  font-weight: normal;
  text-decoration: none;
  align-items: stretch;
  background-color: white;
  justify-content: flex-start;
  min-height: 4em;
  padding: 0;
  margin: 0 0 1.2em 0;
  border-radius: 0.3em;
  overflow: hidden;
  box-shadow: var(--card-box-shadow);
}

.recently-viewed li a:active {
  filter: brightness(0.75) grayscale(1);
}

.recently-viewed .rating-bubble {
  font-size: 0.8em;
}

.no-recents {
  font-size: var(--body);
  padding: 1em 1.4em;
  border-bottom: 1px solid var(--colour-pale-blue);
}

.recently-viewed .cs-details {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  height: auto;
  padding: 0.5em 2.5em 0 0.65em;
  position: relative;
  width: 100%;
}

.recently-viewed .cs-details .rating-bubble {
  position: absolute;
  right: 0.5em;
  top: 0.5em;
}

.recently-viewed .cs-details .rating-bubble:before {
  content: '';
}

.recently-viewed .cs-details .cs-name {
  display: block;
  margin-bottom: 0.1em;
  font-weight: bold;
  font-size: var(--body);
  overflow-wrap: anywhere;
  hyphens: auto;
  line-height: 1.1;
  font-weight: 600;
  color: var(--colour-dark-blue);
}

.image-frame {
  width: 6em;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-shrink: 0;
}

.recently-viewed .cs-location {
  font-size: var(--small);
  margin: 0;
}

.cs-viewed-time {
  align-self: flex-start;
  color: var(--colour-blue);
  font-size: var(--small);
  justify-self: flex-end;
  margin: auto 0 0.5em 0;
  padding-top: 0.5em;
  display: flex;
  align-items: center;
  gap: 0.2em;
}

.cs-viewed-time:before {
  content: '';
  display: inline-block;
  width: 1em;
  height: 1em;
  background: url('~/assets/images/time.svg') no-repeat bottom left;
}

/* desktop & tablet */
.recently-viewed-campsites.desktop {
  width: 100%;
  padding: 0;
}

.recently-viewed-campsites.desktop h3 {
  font-size: 2.25em;
  font-weight: normal;
  text-align: left;
  color: var(--colour-wcag-blue);
  margin: 0.75em auto;
  max-width: var(--page-width-max);
  padding: 1em var(--page-width-padding) 0;
}

.desktop .image-frame {
  width: 100%;
  height: auto;
  aspect-ratio: 4/3;
  border-radius: 0.5em 0.5em 0 0;
}

.recently-viewed-campsites.desktop .cs-details {
  padding: 0.5em 0 0 1em;
  height: 100%;
  display: grid;
}

.recently-viewed-campsites.desktop .cs-viewed-time {
  justify-self: flex-start;
}

.recently-viewed-campsites.desktop .cs-name {
  margin-bottom: 0.25em;
  padding-right: 2.75em;
  font-weight: bold;
}

.desktop .recently-viewed {
  border: none;
  display: flex;
  flex-wrap: nowrap;
  gap: 2em;
  justify-content: flex-start;
  margin: 0;
  max-width: 100dvw;
  overflow-x: auto;
  overflow-y: visible;
  padding: 1em 0 2em var(--page-width-padding);
  position: relative;
  scroll-behavior: smooth;
  scroll-padding-left: var(--page-width-padding);
  scroll-snap-type: x mandatory;
  width: 100%;
}

.desktop .recently-viewed li a {
  display: flex;
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  border: none;
  height: 100%;
  text-decoration: none;
  border-radius: 0.5em;
  background-color: #fff;
}

.desktop .recent {
  padding: 0;
  width: 12em;
  flex: 0 0 auto;
}

.desktop .recent a {
  transition: box-shadow 0.15s ease-out;
}

.desktop .recent:hover a,
.desktop .recent a:focus-visible {
  box-shadow:
    0 0 0 0.33em black,
    var(--card-box-shadow);
}

.desktop .recent a:focus-visible {
  outline: none;
}

/* > 1600 */
@media only screen and (min-width: 1600px) {
  .recently-viewed-campsites.desktop .recently-viewed {
    padding: 1em
      calc((100vw - var(--page-width-max)) / 2 + var(--page-width-padding)) 2em;
    scroll-padding-left: calc(
      (100vw - var(--page-width-max)) / 2 + var(--page-width-padding)
    );
  }
}
</style>
<style>
/* desktop & tablet */
.homepage .recently-viewed-campsites.desktop {
  width: 100%;
  max-width: var(--page-width-max);
  margin: 0 auto;
}

.homepage .recently-viewed-campsites.desktop h3 {
  font-size: 3em;
  font-weight: bold;
  text-align: center;
  margin: 0 auto;
  padding: 1em var(--page-width-padding);
}
</style>
